import { Area } from "../types";
import {
  CoveredRegion,
  ProductCategoryType,
  Region,
  RegionCoveragePricing,
  RegionPricing,
  RegionalDiscountsInput,
} from "@hubcapp/types";

import { filterPricesByProductCategory } from "../AddNewArea/helpers";
import { initialAreaPriceMap } from "../helpers";

export const transformCoverageToArea = (
  coverage: CoveredRegion,
  productCategoryType: ProductCategoryType,
): Area => ({
  id: coverage.region.id,
  name: coverage.region.regionName,
  type: productCategoryType,
  areaCode: coverage.region.regionCode,
  districts: mapDistricts(coverage.region.districts),
  discounts: coverage.discounts,
  numberOfDistricts: coverage.region.districts.length,
  numberOfCoveredDistricts: 0,
  instructorPricing: (coverage.pricing || []) as RegionCoveragePricing[],
  numberOfDiscountsAvailable: coverage.region.discounts?.length,
  numberOfDiscountsApplied: coverage.discounts?.length,
  coverageId: coverage.id,
  pricing: filterPricesByProductCategory(
    productCategoryType,
    coverage?.region?.pricing as RegionPricing[],
  ).map((regionPrice: RegionPricing) =>
    initialAreaPriceMap(
      regionPrice,
      findInstructorPriceByProductCategory(
        coverage.pricing as RegionCoveragePricing[],
        regionPrice.productCategory?.type as ProductCategoryType,
      )?.price || (regionPrice.recommendedPrice as number),
      regionPrice.id,
      regionPrice.productCategory?.type as ProductCategoryType,
    ),
  ),
});

const findInstructorPriceByProductCategory = (
  instructorPricing: RegionCoveragePricing[],
  productCategoryType: ProductCategoryType,
) =>
  (instructorPricing || []).find(
    pricing => pricing.productCategory.type === productCategoryType,
  );

const mapDistricts = (districts: Region["districts"]) =>
  districts
    .filter(district => district?.covered)
    .map(district => ({
      id: district?.id || "",
      postcode: district?.districtCode || "",
      sectors:
        district?.sectors
          .filter(sector => sector?.covered)
          .map(sector => ({
            id: sector?.id || "",
            name: sector?.sectorCode || "",
          })) || [],
    }));

export const mapAreaStateToAreaCoverageMutation = (area: Area) => {
  const preTestPrice = area.pricing?.find(
    pricing => pricing.name === "PreTest",
  );
  const postTestPrice = area.pricing?.find(
    pricing => pricing.name === "PostTest",
  );
  return {
    lineItemCoverageId: area.coverageId || "",
    discounts: area.discounts?.map(discount => ({
      discountId: discount?.id,
      optIn: true,
    })) as RegionalDiscountsInput[],
    districtOptIns:
      area.districts.map(district => ({
        id: district.id,
        covered: true,
        sectorOptOuts:
          district.sectorsToOptOut?.map(sector => ({
            id: sector.id,
            optedOut: true,
          })) || [],
      })) || [],
    preTestPrice: preTestPrice ? parseFloat(preTestPrice.value) : undefined,
    postTestPrice: postTestPrice ? parseFloat(postTestPrice.value) : undefined,
  };
};

export const getSectorIdsFromDistricts = (districts: Area["districts"]) =>
  districts.reduce((sectors: string[], district) => {
    const sectorIds = district?.sectors.map(sector => sector.id);
    if (sectorIds?.length) {
      sectors.push(...sectorIds);
    }

    return sectors;
  }, []);

export const transformAreaToFormValues = (area: Area) => ({
  sectors: getSectorIdsFromDistricts(area.districts),
  discounts: area.discounts ? area.discounts.map(discount => discount?.id) : [],
  pricing: area.pricing,
});
